import { TFunction } from 'i18next';
import { MenuItemType } from '../components/common/layout/types';
import { LABELS_NS } from './consts';

export const listPathsOfIndustries = {
  health: '/industries/health-med-tech',
  visionPro: '/industries/apple-vision-pro-app-development-services',
  aws: '/industries/amazon-web-services-aws-cloud-dev',
  education: '/industries/education-software-development-services',
  retail: '/industries/retail-software-development-services',
  media: '/industries/media-and-entertainment-software-development',
  fintech: '/industries/fintech-software-development-services',
  hr: '/industries/hr-software-development-services',
  ecommerce: '/industries/ecommerce-software-development-services',
  telemedicine: '/industries/telemedicine-app-development-services',
  cloudCost: '/industries/cloud-cost-optimization-services',
  ehrAndEmr: '/industries/ehr-and-emr-software-development',
  serverless: '/industries/serverless-application-development-services',
  healthInfoExchange: '/industries/health-information-exchange-software',
  mentalHealth: '/industries/mental-health-app-development-services',
  fitnessApp: '/industries/fitness-app-development-services',
  wellnessApp: '/industries/wellness-app-development-services',
  oculusMetaQuest: '/industries/oculus-meta-quest-app-development-services',
  sportsAppDev: '/industries/sports-app-development-company',
};

export const getPaths = (t: TFunction) => [
  {
    path: '/industries',
    name: t('industries'),
    withSubMenu: true,
  },
  {
    path: '/services',
    name: t('services'),
    withSubMenu: true,
  },
  {
    path: '/technologies',
    name: t('technologies'),
    withSubMenu: true,
  },
  {
    path: '/cases',
    name: t('cases'),
    withSubMenu: true,
    // The submenu lists case catalogs, which live under /cases/category/:slug
    // while the menu item itself still points at the flat /cases listing.
    subMenuBasePath: '/cases/category',
  },
  {
    path: '/faq',
    name: t('faq', { ns: LABELS_NS }),
  },
  {
    path: '/blog',
    name: t('blog'),
  },
  {
    path: '/about',
    name: t('aboutUs'),
  },
];

export type Path = {
  readonly path: string;
  readonly name: string;
  readonly withSubMenu?: boolean;
  readonly subMenuBasePath?: string;
  readonly image?: string;
  readonly logo?: string;
};

export const getHeaderCompanyNavItems = (t: TFunction): MenuItemType[] =>
  [
    {
      title: t('aboutUs'),
      slug: 'about',
    },
    // Cases is a top-level submenu of its own now, so it is not repeated here.
    {
      title: t('faq', { ns: LABELS_NS }),
      slug: 'faq',
    },
    {
      title: t('blog'),
      slug: 'blog',
    },
    {
      title: t('partnership'),
      slug: 'partnership',
    },
  ] as const;

export const getFooterNavItems = (t: TFunction) =>
  [
    {
      title: t('home'),
      link: '/',
    },
    {
      title: t('aboutUs'),
      link: '/about',
    },
    {
      title: t('cases'),
      link: '/cases',
    },
    {
      title: t('careers'),
      link: '/careers',
    },
    {
      title: t('blog'),
      link: '/blog',
    },
    {
      title: t('contact'),
      link: '/contact',
    },
    {
      title: t('partnership'),
      link: '/partnership',
    },
    {
      title: t('secureDevelopmentEnvironment'),
      link: '/secure-development-environment',
    },
  ] as const;

export const pathToHome = '/';
export const pathToAuthors = '/authors';
export const pathToCases = '/cases';
// Case catalogs are nested under /cases; there is no listing page at this path
// itself, CloudFront 301s it to /cases.
export const pathToCatalog = `${pathToCases}/category`;
export const pathToContact = '/contact';
export const pathToCareers = '/careers';
export const pathToAboutUs = '/about';
export const pathToPrivacyPolicy = '/privacy-policy';
export const pathToRefundPolicy = '/refund-policy';
export const pathToSitemap = '/sitemap';
export const pathToTermsAndConditions = '/terms-and-conditions';
export const pathToBlog = '/blog';
export const pathToServices = '/services';
export const pathToCVSent = `${pathToCareers}/cv-sent`;
export const pathToIndustries = '/industries';
export const pathToTags = '/blog/tags';
export const pathToErrorSent = '/error-sent';
export const pathToTechnologies = '/technologies';
export const pathToPartnership = '/partnership';
export const pathToFaq = '/faq';
export const pathToNotFoundPage = '/404';
// redirect paths
export const pathToGoogleRedirect = '/connect/google/redirect';
export const pathToFacebookRedirect = '/connect/facebook/redirect';
export const pathToLinkedinRedirect = '/connect/linkedin/redirect';
export const pathToTwitterRedirect = '/connect/twitter/redirect';

export const getServicesPageName = (t: TFunction) => t('services');
export const getIndustriesPageName = (t: TFunction) => t('industries');
export const getTechnologiesPageName = (t: TFunction) => t('technologies');
